import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AccountController } from 'src/@core/APIs/AccountController';
import { BaseURL } from 'src/@core/config';
import { AuthService } from 'src/@core/services/auth.service';
import { decodeJwt } from 'src/@core/utils/jwt';

@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
  private readonly retryHeader = 'X-Auth-Retry';

  constructor(private readonly authService: AuthService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    const user = this.authService.getCurrentUser();
    const request = this.withAuthorization(req, user);

    if (user?.token && this.isTokenExpired(user.token) && this.authService.hasRefreshToken()) {
      return this.authService.refreshAccessToken().pipe(
        switchMap((refreshedUser) =>
          next.handle(this.withAuthorization(req, refreshedUser))
        ),
        catchError((err) => {
          this.authService.logout();
          return throwError(() => err);
        })
      );
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!this.shouldRefreshAfterError(req, err)) {
          return throwError(() => err);
        }

        return this.authService.refreshAccessToken().pipe(
          switchMap((refreshedUser) =>
            next.handle(
              this.withAuthorization(req, refreshedUser).clone({
                setHeaders: { [this.retryHeader]: '1' },
              })
            )
          ),
          catchError((refreshErr) => {
            this.authService.logout();
            return throwError(() => refreshErr);
          })
        );
      })
    );
  }

  private shouldRefreshAfterError(req: HttpRequest<unknown>, err: HttpErrorResponse): boolean {
    return (
      Number(err?.status) === 401 &&
      !req.headers.has(this.retryHeader) &&
      !this.shouldSkip(req) &&
      this.authService.hasRefreshToken()
    );
  }

  private shouldSkip(req: HttpRequest<unknown>): boolean {
    const url = req.url.toLowerCase();
    return (
      !this.isForLabApiUrl(url) ||
      url === AccountController.Login.toLowerCase() ||
      url === AccountController.RefreshToken.toLowerCase() ||
      url === AccountController.Logout.toLowerCase()
    );
  }

  private isForLabApiUrl(url: string): boolean {
    return Boolean(BaseURL) && url.startsWith(BaseURL.toLowerCase());
  }

  private withAuthorization(req: HttpRequest<unknown>, user: any): HttpRequest<unknown> {
    const token = user?.token;
    if (!token) {
      return req;
    }

    const bearerToken = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
    return req.clone({
      setHeaders: {
        Authorization: bearerToken,
      },
    });
  }

  private isTokenExpired(token: string): boolean {
    const raw = token.replace(/^Bearer\s+/i, '');
    const payload = decodeJwt(raw);
    if (!payload?.exp) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    return Number(payload.exp) <= now;
  }
}
